import { BreakoutExperienceWidget } from 'components/BreakoutExperience'
import { useBreakoutUser } from 'hooks/profile'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import classNames from 'classnames'
import { useRootStore } from 'hooks/rootStore'
import { when } from 'mobx'
import { useTranslationTyped } from 'i18n/i18n'
import { PageTitle } from 'components/PageTitle'

const OnboardingPortraitViewContext = createContext(false)

const useOnboardingPortraitView = () =>
  useContext(OnboardingPortraitViewContext)

export const OnboardingPage = () => {
  const user = useBreakoutUser()
  const [portraitView, setPortraitView] = useState(false)
  const { tt } = useTranslationTyped()
  const store = useRootStore()

  const setOnboardCompleteAndRedirect = useCallback(async () => {
    user.setOnboardingComplete()
    // wait for shouldShowOnboarding update to propagate
    // so we don't get falsely sent back to onboarding from the redirect handler
    await when(() => user.shouldShowOnboarding === false)
    store.navigateTo('home')
  }, [user, store])

  useEffect(() => {
    const setPortraitModeOnResize = () => {
      if (window.innerHeight > window.innerWidth && window.innerWidth < 850) {
        return setPortraitView(true)
      } else {
        setPortraitView(false)
      }
    }
    // run once on load
    setPortraitModeOnResize()
    window.addEventListener('resize', setPortraitModeOnResize)
    return () => {
      window.removeEventListener('resize', setPortraitModeOnResize)
    }
  }, [])

  return (
    <div className="flex h-full w-full items-center justify-center p-[0.75rem]">
      <div
        className={classNames(
          'h-full w-full max-w-[75rem] rounded-3xl bg-core-tertiary',
          {
            'max-h-[43.75rem]': !portraitView,
          }
        )}
      >
        <OnboardingPortraitViewContext.Provider value={portraitView}>
          <PageTitle title={tt.onboarding.onboarding()} />
          <BreakoutExperienceWidget
            includeBackButton={true}
            components={[Page1, Page2, Page4, Page5, Page6]}
            doneButtonText={tt.onboarding.continue()}
            onDone={setOnboardCompleteAndRedirect}
            onSkip={setOnboardCompleteAndRedirect}
          />
        </OnboardingPortraitViewContext.Provider>
      </div>
    </div>
  )
}

const OnboardingScreenWithImageTemplate = ({
  imageSrc,
  title,
  subtitle,
  includeImageShadow = true,
  includeDropShadow = false,
}: {
  imageSrc: string
  title: string
  subtitle: string
  includeImageShadow?: boolean
  includeDropShadow?: boolean
}) => {
  const portraitView = useOnboardingPortraitView()
  return (
    <div
      className={classNames('flex h-full w-full items-center justify-center', {
        //todo: sort out padding top/bot with no more absolute positioning
        //'px-3': portraitView,
        //'px-12': !portraitView,
      })}
    >
      <div
        className={classNames(
          'flex h-full w-full items-center justify-center',
          {
            'flex-col-reverse justify-evenly': portraitView,
            'flex-row justify-center gap-[3.75rem]': !portraitView,
          }
        )}
      >
        {/* Title Subtitle */}
        <div
          className={classNames('flex flex-col', {
            'flex-[7]': !portraitView,
            'mt-5': portraitView,
          })}
        >
          <h1 data-testid="onboarding-title" className={`mb-3 text-body-large`}>
            {title}
          </h1>
          <p className={`text-headline-medium sm:text-headline-large`}>
            {subtitle}
          </p>
        </div>
        {/* Image */}
        <div
          className={classNames('max-h-full min-h-0', {
            'flex h-full min-h-0 w-full min-w-0 flex-[5] items-center':
              !portraitView,
          })}
        >
          <img
            src={imageSrc}
            alt=""
            className={classNames(
              'mx-auto my-auto max-h-full max-w-full justify-center object-contain',
              {
                'shadow-3xl': includeImageShadow,
                'rounded-3xl': includeImageShadow,
                'drop-shadow-3xl': includeDropShadow,
                'h-full w-full': portraitView,
              }
            )}
          />
        </div>
      </div>
    </div>
  )
}

const Page1 = () => {
  const { tt } = useTranslationTyped()
  return (
    <div className="flex h-full flex-col items-center justify-center">
      <h1 data-testid="onboarding-title" className="mb-3 text-body-large">
        {tt.onboarding.welcome().toUpperCase()}
      </h1>
      <p className="max-w-[46.875rem] text-center text-headline-medium sm:text-headline-large">
        {tt.onboarding.welcome_sub()}
      </p>
    </div>
  )
}

const Page2 = () => {
  const { tt } = useTranslationTyped()
  return (
    <OnboardingScreenWithImageTemplate
      title={tt.onboarding.class_assignments()}
      subtitle={tt.onboarding.class_assignments_sub()}
      imageSrc="/assets/images/onboarding/onboarding_02.png"
      includeImageShadow={false}
      includeDropShadow={true}
    />
  )
}

const Page4 = () => {
  const { tt } = useTranslationTyped()
  return (
    <OnboardingScreenWithImageTemplate
      title={tt.onboarding.groups()}
      subtitle={tt.onboarding.groups_sub()}
      imageSrc="/assets/images/onboarding/onboarding_04.png"
    />
  )
}

const Page5 = () => {
  const { tt } = useTranslationTyped()
  return (
    <OnboardingScreenWithImageTemplate
      title={tt.onboarding.prework()}
      subtitle={tt.onboarding.prework_sub()}
      imageSrc="/assets/images/onboarding/onboarding_05.png"
    />
  )
}

const Page6 = () => {
  const { tt } = useTranslationTyped()
  return (
    <OnboardingScreenWithImageTemplate
      title={tt.onboarding.experience()}
      subtitle={tt.onboarding.experience_sub()}
      imageSrc="/assets/images/onboarding/onboarding_06.png"
    />
  )
}
